import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  IconButton,
  CardContent,
  DialogActions,
  TextField,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import {
  createLeadRequest,
  updateLeadRequest,
} from "../../../redux/Required/requiredSlice";
import { fetchJewellersSidebarRequest } from "../../../redux/JewellerList/JewellerSlice";
import { getActiveCountriesRequest } from "../../../redux/CountryList/CountrySlice";
import LeadList from "./LeadList";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { canShowPermission } from "../Permissions/permissionObject";
import Autocomplete from "@mui/material/Autocomplete";
import auth from "../../../configs/auth";
import { fetchInstalaxmiAdminRequest } from "../../../redux/Tickets/ticketSlice";
import { getConfiguration } from "../../../redux/Configuration/configurationSlice";

const designationOptions = [
  "Owner",
  "Account Manager",
  "Store Manager",
  "Other",
];

const SOURCE_SHOW = "Show";
const SOURCE_FIELD_VISIT = "Field Visit";

const mapShowNames = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) =>
      String(typeof item === "string" ? item : item?.name || "").trim(),
    )
    .filter(Boolean);
};

const isFieldVisitSource = (value) =>
  String(value || "").trim().toLowerCase() === "field visit";

const CreateLeads = () => {
  const dispatch = useDispatch();
  const instalaxmiAdminList = useSelector(
    (state) => state.tickets?.instalaxmiAdmin,
  );
  const instalaxmiAdmin = Array.isArray(instalaxmiAdminList)
    ? instalaxmiAdminList
    : [];
  const jewellersSidebar =
    useSelector((state) => state.jeweller.jewellersSidebar) || [];
  const { activeCountries = [] } = useSelector((state) => state.country || {});
  const configurationData = useSelector(
    (state) => state.appConfig.configurationData,
  );
  const showNameOptions = mapShowNames(configurationData?.showNames);
  const selectedJewellerId = sessionStorage.getItem(auth.selectedJeweller);

  const [filteredStates, setFilteredStates] = useState([]);
  const [selectedPhoneLength, setSelectedPhoneLength] = useState(10);
  const [selectedDialCode, setSelectedDialCode] = useState("+91");
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreateLeads = canShowPermission(permissions, "leads", "create");

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const emptyJewellerData = {
    _id: null,
    jewellerName: "",
    jeweller: "",
    countryCode: "",
    contactName: "",
    designation: "",
    otherDesignation: "",
    contactNumbers: [{ number: "", isPrimary: true }],
    comment: "",
    RM: [],
    accountOwner: [],
    email: "",
    sourceType: "",
    source: "",
    address: [{ Area: "", state: "", city: "", pincode: "" }],
  };

  const [jewellerData, setJewellerData] = useState(emptyJewellerData);
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const jewellerID = storedUser?.jeweller;

  useEffect(() => {
    dispatch(fetchInstalaxmiAdminRequest(jewellerID));
  }, [dispatch, jewellerID]);

  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest()); // jeweller list
    dispatch(getActiveCountriesRequest({ jeweller: selectedJewellerId }));
    dispatch(getConfiguration());
  }, [dispatch]);

  const handleOpenDialog = () => setOpenDialog(true);

  useEffect(() => {
    if (!openDialog || isEdit) return;
    const ownerId = instalaxmiAdmin.find(
      (user) => user._id === storedUser?._id,
    )?._id;
    if (!ownerId) return;
    setJewellerData((prev) =>
      prev.accountOwner?.length ? prev : { ...prev, accountOwner: [ownerId] },
    );
  }, [openDialog, isEdit, instalaxmiAdmin, storedUser?._id]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEdit(false);
    setFilteredStates([]);
    setSelectedPhoneLength(10);
    setSelectedDialCode("+91");
    setJewellerData(emptyJewellerData);
  };

  // Prefill form for edit
  const handleEditClick = (leadRecord) => {
    const countryObj = Array.isArray(leadRecord.countryCode)
      ? leadRecord.countryCode[0]
      : null;
    const countryId = countryObj?._id || leadRecord.countryCode || "";
    console.log("Editing lead:", leadRecord, "Country ID:", countryId); // Debugging line

    const matchedActiveCountry = activeCountries.find(
      (a) => a._id === countryId,
    );

    setFilteredStates(matchedActiveCountry?.states || []);
    setSelectedPhoneLength(matchedActiveCountry?.phoneLength || 10);
    setSelectedDialCode(matchedActiveCountry?.dialCode || "+91");

    const savedDesignation = leadRecord.designation || "";
    const matchedDesignation = designationOptions.includes(savedDesignation)
      ? savedDesignation
      : savedDesignation
        ? "Other"
        : "";

    setJewellerData({
      _id: leadRecord._id,
      jewellerName: leadRecord.jewellerName || "",
      jeweller: leadRecord.jeweller?._id || leadRecord.jeweller || "",
      countryCode: countryId,
      contactName: leadRecord.contactName || "",
      designation: matchedDesignation,
      otherDesignation: matchedDesignation === "Other" ? savedDesignation : "",
      contactNumbers: [
        {
          number: leadRecord.contactNumber || leadRecord.phone || "",
          isPrimary: true,
        },
        ...(leadRecord.alternatePhones || [])
          .map((item) => (typeof item === "string" ? item : item?.number))
          .filter((number) => number && number !== leadRecord.contactNumber)
          .map((number) => ({ number, isPrimary: false })),
      ],
      comment: leadRecord.comment || "",
      RM: (Array.isArray(leadRecord.assignedUser)
        ? leadRecord.assignedUser
        : leadRecord.assignedUser
          ? [leadRecord.assignedUser]
          : Array.isArray(leadRecord.assignTo)
            ? leadRecord.assignTo
            : leadRecord.assignTo
              ? [leadRecord.assignTo]
              : []
      )
        .map((item) => (typeof item === "string" ? item : item?._id))
        .filter(Boolean),
      accountOwner: (Array.isArray(leadRecord.accountOwner)
        ? leadRecord.accountOwner
        : leadRecord.accountOwner
          ? [leadRecord.accountOwner]
          : []
      )
        .map((item) => (typeof item === "string" ? item : item?._id))
        .filter(Boolean),
      email: leadRecord.email || "",
      sourceType: leadRecord.source
        ? isFieldVisitSource(leadRecord.source)
          ? SOURCE_FIELD_VISIT
          : SOURCE_SHOW
        : "",
      source: isFieldVisitSource(leadRecord.source)
        ? SOURCE_FIELD_VISIT
        : leadRecord.source || "",
      address: [
        {
          Area: leadRecord.area || "",
          state: leadRecord.state || "",
          city: leadRecord.city || "",
          pincode: leadRecord.pincode || "",
        },
      ],
    });
    setIsEdit(true);
    setOpenDialog(true);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("address[0].")) {
      const key = name.split(".")[1];
      setJewellerData((prev) => ({
        ...prev,
        address: [{ ...prev.address[0], [key]: value }],
      }));
    } else {
      setJewellerData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle Create or Update
  const handleSaveJeweller = () => {
    const { jewellerName, contactName, email, address, RM, _id, jeweller } =
      jewellerData;
    console.log("jewellerData:", jewellerData); // Debugging line

    const nameRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!jewellerName.trim()) {
      toast.error("Jeweller Name is required");
      return;
    }

    if (!contactName.trim()) {
      toast.error("Contact Name is required");
      return;
    }
    if (!nameRegex.test(contactName.trim())) {
      toast.error("Contact Name must contain only letters and spaces");
      return;
    }
    if (
      jewellerData.designation === "Other" &&
      !(jewellerData.otherDesignation || "").trim()
    ) {
      toast.error("Please enter designation");
      return;
    }
    if (jewellerData.sourceType === SOURCE_SHOW && !(jewellerData.source || "").trim()) {
      toast.error("Please select a show name");
      return;
    }

    const contacts = (jewellerData.contactNumbers || []).filter((item) =>
      item.number?.trim(),
    );
    if (!contacts.length) {
      toast.error("Phone number is required");
      return;
    }
    if (!jewellerData.countryCode) {
      toast.error("Please select a Country");
      return;
    }
    if (contacts.some((item) => item.number.length !== selectedPhoneLength)) {
      toast.error(`Phone number must be exactly ${selectedPhoneLength} digits`);
      return;
    }
    if (!contacts.some((item) => item.isPrimary)) {
      toast.error("Please select a primary contact number");
      return;
    }

    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!address[0].Area.trim()) {
      toast.error("Area is required");
      return;
    }

    if (!address[0].state.trim()) {
      toast.error("State is required");
      return;
    }

    if (!address[0].city.trim()) {
      toast.error("City is required");
      return;
    }

    if (!address[0].pincode.trim()) {
      toast.error("Pincode is required");
      return;
    }

    if (!Array.isArray(RM) || RM.length === 0) {
      toast.error("Please select Assign To");
      return;
    }

    if (isEdit && !_id) {
      toast.error("Lead ID is missing for edit action");
      return;
    }

    const leadPayload = {
      id: jewellerData._id,
      assignTo: (Array.isArray(jewellerData.RM)
        ? jewellerData.RM
        : [jewellerData.RM]
      )
        .map((item) => (typeof item === "string" ? item : item?._id))
        .filter(Boolean),
      accountOwner: (Array.isArray(jewellerData.accountOwner)
        ? jewellerData.accountOwner
        : []
      )
        .map((item) => (typeof item === "string" ? item : item?._id))
        .filter(Boolean),
      jeweller: jewellerData.jeweller || "",
      jewellerName: jewellerData.jewellerName,
      countryCode: jewellerData.countryCode,
      contactName: jewellerData.contactName,
      contactNumber: (contacts.find((item) => item.isPrimary) || contacts[0])
        .number,
      alternatePhones: contacts.filter((item) => !item.isPrimary).map((item) => item.number),
      email: jewellerData.email,
      area: jewellerData.address[0].Area,
      state: jewellerData.address[0].state,
      city: jewellerData.address[0].city,
      pincode: jewellerData.address[0].pincode,
      designation: jewellerData.designation,
    };

    const source = (jewellerData.source || "").trim();
    if (source) {
      leadPayload.source = source;
    } else if (isEdit) {
      leadPayload.source = "";
    }

    const comment = (jewellerData.comment || "").trim();
    if (comment) {
      leadPayload.comment = comment;
    } else if (isEdit) {
      leadPayload.comment = "";
    }

    const designation =
      jewellerData.designation === "Other"
        ? (jewellerData.otherDesignation || "").trim()
        : (jewellerData.designation || "").trim();
    if (designation) {
      leadPayload.designation = designation;
    } else if (isEdit) {
      leadPayload.designation = "";
    }

    if (isEdit) {
      dispatch(updateLeadRequest(leadPayload));
      // toast.success('Leads Updated successfully!');
    } else {
      dispatch(createLeadRequest(leadPayload));
      // toast.success('Leads created successfully!');
    }

    handleCloseDialog();
  };

  return (
    <Box sx={{ width: "98%" }}>
      <Box
        sx={{
          width: "100%",
          mt: { xs: 10, md: 10 },
          mb: { xs: 2, md: 2 },
          background: "#fff",
          borderRadius: "3px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            justifyContent: "space-between",
            display: "flex",
            p: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: "24px", fontWeight: 700, fontFamily: "Aeonik" }}
            >
              Leads List
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
              Manage leads, follow-ups, and jeweller conversion
            </Typography>
          </Box>
          {canCreateLeads && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 2,
                background:
                  "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
                boxShadow: "0 4px 12px rgba(122, 63, 255, 0.25)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #5f1fc0 0%, #8a32c2 76.93%)",
                },
              }}
              onClick={handleOpenDialog}
            >
              New Lead
            </Button>
          )}
        </Box>
        <CardContent sx={{ width: "100%" }}>
          <LeadList onEdit={handleEditClick} />
        </CardContent>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "3px" } }}
      >
        <DialogTitle
          fontFamily={"Aeonik"}
          fontWeight={700}
          fontSize={24}
          sx={{
            background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
            color: "#fff",
          }}
        >
          {isEdit ? "Update Lead" : "Create Lead"}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            fullWidth
            options={jewellersSidebar}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.jewellerName || ""
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            inputValue={jewellerData.jewellerName}
            onInputChange={(event, newInputValue) => {
              // Try to auto-resolve typed text against the sidebar list
              const matched = jewellersSidebar.find(
                (j) =>
                  j.jewellerName?.trim().toLowerCase() ===
                  newInputValue.trim().toLowerCase(),
              );

              setJewellerData((prev) => ({
                ...prev,
                jewellerName: newInputValue,
                jeweller: matched ? matched.id : "", // no match -> new jeweller, id stays empty
              }));
            }}
            onChange={(event, newValue) => {
              if (!newValue) {
                // cleared
                setJewellerData((prev) => ({
                  ...prev,
                  jewellerName: "",
                  jeweller: "",
                }));
                return;
              }

              if (typeof newValue === "string") {
                // freeSolo commit (Enter key) — resolve against the list too
                const matched = jewellersSidebar.find(
                  (j) =>
                    j.jewellerName?.trim().toLowerCase() ===
                    newValue.trim().toLowerCase(),
                );
                setJewellerData((prev) => ({
                  ...prev,
                  jewellerName: newValue,
                  jeweller: matched ? matched.id : "",
                }));
              } else {
                // picked directly from the dropdown
                setJewellerData((prev) => ({
                  ...prev,
                  jewellerName: newValue.jewellerName || "",
                  jeweller: newValue.id || "",
                }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Jeweller Shop Name"
                name="jewellerName"
              />
            )}
          />

          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Account Owner</InputLabel>
            <Select
              name="accountOwner"
              label="Account Owner"
              value={jewellerData.accountOwner?.[0] || ""}
              onChange={(e) =>
                setJewellerData((prev) => ({
                  ...prev,
                  accountOwner: e.target.value ? [e.target.value] : [],
                }))
              }
              renderValue={(id) =>
                instalaxmiAdmin.find((user) => user._id === id)?.name || ""
              }
            >
              {instalaxmiAdmin.length === 0 ? (
                <MenuItem disabled value="">
                  No admins available
                </MenuItem>
              ) : (
                instalaxmiAdmin.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Support Manager</InputLabel>
            <Select
              name="RM"
              label="Support Manager"
              value={jewellerData.RM?.[0] || ""}
              onChange={(e) =>
                setJewellerData((prev) => ({
                  ...prev,
                  RM: e.target.value ? [e.target.value] : [],
                }))
              }
              renderValue={(id) =>
                instalaxmiAdmin.find((user) => user._id === id)?.name || ""
              }
            >
              {instalaxmiAdmin.length === 0 ? (
                <MenuItem disabled value="">
                  No admins available
                </MenuItem>
              ) : (
                instalaxmiAdmin.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Country dropdown — now driven entirely by activeCountries */}
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Country</InputLabel>
            <Select
              name="countryCode"
              value={jewellerData.countryCode || ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                const matchedActive = activeCountries.find(
                  (a) => a._id === selectedId,
                );

                if (!matchedActive) {
                  toast.error("Selected country is not active");
                  return;
                }

                setSelectedPhoneLength(matchedActive.phoneLength || 10);
                setSelectedDialCode(matchedActive.dialCode || "+91");
                setFilteredStates(matchedActive.states || []);

                setJewellerData((prev) => ({
                  ...prev,
                  countryCode: matchedActive._id,
                  address: [{ ...prev.address[0], state: "" }],
                }));
              }}
            >
              {activeCountries.map((country) => (
                <MenuItem key={country._id} value={country._id}>
                  {`${country.countryName} (${country.countryCode})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="dense"
            label="Contact Person Name"
            name="contactName"
            value={jewellerData.contactName}
            onChange={handleChange}
          />

          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Designation</InputLabel>
            <Select
              name="designation"
              label="Designation"
              value={jewellerData.designation || ""}
              onChange={(e) => {
                const value = e.target.value;
                setJewellerData((prev) => ({
                  ...prev,
                  designation: value,
                  otherDesignation:
                    value === "Other" ? prev.otherDesignation : "",
                }));
              }}
            >
              {designationOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {jewellerData.designation === "Other" && (
            <TextField
              fullWidth
              margin="dense"
              label="Other Designation"
              name="otherDesignation"
              value={jewellerData.otherDesignation || ""}
              onChange={handleChange}
            />
          )}

          {(jewellerData.contactNumbers || []).map((item, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
            >
              <TextField
                sx={{ flexShrink: 0, width: "72px" }}
                margin="dense"
                size="small"
                value={selectedDialCode}
                disabled
              />
              <TextField
                fullWidth
                margin="dense"
                size="small"
                label="Contact Number"
                placeholder={`${selectedPhoneLength} digits`}
                value={item.number}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, "");
                  if (raw.length > selectedPhoneLength) return;
                  setJewellerData((prev) => ({
                    ...prev,
                    contactNumbers: prev.contactNumbers.map((row, rowIndex) =>
                      rowIndex === index ? { ...row, number: raw } : row,
                    ),
                  }));
                }}
              />
              <FormControlLabel
                sx={{ m: 0, flexShrink: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={!!item.isPrimary}
                    onChange={() => {
                      if (item.isPrimary) return;
                      setJewellerData((prev) => ({
                        ...prev,
                        contactNumbers: prev.contactNumbers.map(
                          (row, rowIndex) => ({
                            ...row,
                            isPrimary: rowIndex === index,
                          }),
                        ),
                      }));
                    }}
                  />
                }
                label="Primary"
              />
              {jewellerData.contactNumbers.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() =>
                    setJewellerData((prev) => {
                      const next = prev.contactNumbers.filter(
                        (_, rowIndex) => rowIndex !== index,
                      );
                      const hasPrimary = next.some((row) => row.isPrimary);
                      return {
                        ...prev,
                        contactNumbers: next.map((row, rowIndex) => ({
                          ...row,
                          isPrimary: hasPrimary
                            ? row.isPrimary
                            : rowIndex === 0,
                        })),
                      };
                    })
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              setJewellerData((prev) => ({
                ...prev,
                contactNumbers: [
                  ...prev.contactNumbers,
                  { number: "", isPrimary: false },
                ],
              }))
            }
            sx={{ textTransform: "none", mt: 0.5 }}
          >
            Add contact number
          </Button>

          <TextField
            fullWidth
            margin="dense"
            label="Email"
            name="email"
            value={jewellerData.email}
            onChange={handleChange}
          />

          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>State</InputLabel>
            <Select
              name="address[0].state"
              value={jewellerData.address[0].state}
              onChange={handleChange}
            >
              {filteredStates.length > 0 ? (
                filteredStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="">Select a country first</MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="dense"
            label="City"
            name="address[0].city"
            value={jewellerData.address[0].city}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Area"
            name="address[0].Area"
            value={jewellerData.address[0].Area}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Pincode"
            name="address[0].pincode"
            value={jewellerData.address[0].pincode}
            onChange={handleChange}
          />

          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Source</InputLabel>
            <Select
              label="Source"
              value={jewellerData.sourceType || ""}
              onChange={(e) => {
                const value = e.target.value;
                setJewellerData((prev) => ({
                  ...prev,
                  sourceType: value,
                  source: value === SOURCE_FIELD_VISIT ? SOURCE_FIELD_VISIT : "",
                }));
              }}
            >
              <MenuItem value={SOURCE_SHOW}>{SOURCE_SHOW}</MenuItem>
              <MenuItem value={SOURCE_FIELD_VISIT}>{SOURCE_FIELD_VISIT}</MenuItem>
            </Select>
          </FormControl>
          {jewellerData.sourceType === SOURCE_SHOW && (
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Show Name</InputLabel>
              <Select
                label="Show Name"
                value={jewellerData.source || ""}
                onChange={(e) =>
                  setJewellerData((prev) => ({
                    ...prev,
                    source: e.target.value,
                  }))
                }
              >
                {showNameOptions.length > 0 ? (
                  showNameOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    No show names found
                  </MenuItem>
                )}
                {jewellerData.source &&
                  !showNameOptions.includes(jewellerData.source) && (
                    <MenuItem value={jewellerData.source}>
                      {jewellerData.source}
                    </MenuItem>
                  )}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            margin="dense"
            label="Comment"
            name="comment"
            value={jewellerData.comment || ""}
            onChange={handleChange}
            multiline
            minRows={2}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: "none",
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
              WebkitBackgroundClip: "text",
              fontWeight: "700",
              WebkitTextFillColor: "transparent",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveJeweller}
            sx={{
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
            }}
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateLeads;
